import axios from 'axios';
import { baseUrl, HttpMethods } from '../../base';
import type {
  GetDocumentResponse,
  GetDocumentService,
  GetDocumentsResponse,
  GetDocumentsService,
  GetDrawResponse,
  GetDrawService,
  GetDrawsResponse,
  GetDrawsService,
  GetInvoiceResponse,
  GetInvoiceService,
  GetInvoicesResponse,
  GetInvoicesService,
  GetLatestDrawService,
  PatchDocumentResponse,
  PatchDocumentService,
  PatchDrawResponse,
  PatchDrawService,
  PatchInvoiceResponse,
  PatchInvoiceService,
  PostNewDocumentService,
  PostNewDrawService,
  PostNewInvoiceService,
} from './types';

export async function postNewDraw({ projectId, data }: PostNewDrawService) {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws`,
    data,
  });

  return res.data;
}

export async function getDraws({ projectId, params }: GetDrawsService) {
  const res = await axios.request<GetDrawsResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws`,
    params,
  });

  return res.data;
}

export async function getDraw({ projectId, drawId }: GetDrawService) {
  const res = await axios.request<GetDrawResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}`,
  });

  return res.data;
}

export async function getDrawDropdown({ projectId }: Partial<GetDrawService>) {
  const res = await axios.request<GetDrawResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/drop-down`,
  });

  return res.data;
}

export async function patchDraw({ projectId, drawId, data }: PatchDrawService) {
  const res = await axios.request<PatchDrawResponse>({
    method: HttpMethods.PATCH,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}`,
    data,
  });

  return res.data;
}

export async function deleteDraw({ projectId, drawId }: GetDrawService) {
  const res = await axios.request<PatchInvoiceResponse>({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}`,
  });

  return res.data;
}

export async function postNewDocument({ projectId, data, drawId }: PostNewDocumentService) {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/documents`,
    data,
  });

  return res.data;
}

export async function getDocuments({ projectId, params, drawId }: GetDocumentsService) {
  const res = await axios.request<GetDocumentsResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/documents`,
    params,
  });

  return res.data;
}

export async function getDocument({ projectId, drawId, documentId }: GetDocumentService) {
  const res = await axios.request<GetDocumentResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/documents/${documentId}`,
  });

  return res.data;
}

export async function deleteDocument({ projectId, drawId, documentId }: GetDocumentService) {
  const res = await axios.request<PatchInvoiceResponse>({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/documents/${documentId}`,
  });

  return res.data;
}

export async function getDocumentTypes() {
  const res = await axios.request({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/document-types`,
  });

  return res.data;
}

export async function postNewInvoice({ projectId, data, drawId }: PostNewInvoiceService) {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices`,
    data,
  });

  return res.data;
}

export async function getInvoices({ projectId, params, drawId }: GetInvoicesService) {
  const res = await axios.request<GetInvoicesResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices`,
    params,
  });

  return res.data;
}

export async function patchDocument({ projectId, drawId, documentId, data }: PatchDocumentService) {
  const res = await axios.request<PatchDocumentResponse>({
    method: HttpMethods.PUT,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/documents/${documentId}`,
    data,
  });

  return res.data;
}

export async function getInvoice({ projectId, drawId, invoiceId }: GetInvoiceService) {
  const res = await axios.request<GetInvoiceResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices/${invoiceId}`,
  });

  return res.data;
}

export async function patchInvoice({ projectId, drawId, invoiceId, data }: PatchInvoiceService) {
  const res = await axios.request<PatchInvoiceResponse>({
    method: HttpMethods.PATCH,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices/${invoiceId}`,
    data,
  });

  return res.data;
}

export async function putInvoice({ projectId, drawId, invoiceId, data }: PatchInvoiceService) {
  const res = await axios.request<PatchInvoiceResponse>({
    method: HttpMethods.PUT,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices/${invoiceId}`,
    data,
  });

  return res.data;
}

export async function deleteInvoice({ projectId, drawId, invoiceId }: GetInvoiceService) {
  const res = await axios.request<PatchInvoiceResponse>({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices/${invoiceId}`,
  });

  return res.data;
}

export async function putInvoiceDocuments({ projectId, drawId, invoiceId, data }: PatchInvoiceService) {
  const res = await axios.request<PatchInvoiceResponse>({
    method: HttpMethods.PUT,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/${drawId}/invoices/${invoiceId}`,
    data,
  });

  return res.data;
}

export async function getInvoicesTypes() {
  const res = await axios.request({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/document-types`,
  });

  return res.data;
}

export async function getLatestCreatedDraw({ projectId }: GetLatestDrawService) {
  const res = await axios.request({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/${projectId}/draws/last-created`,
  });

  return res.data;
}
